import { flow } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { getProp } from '@mews/fp';
import { ResolveRoute } from '@mews/routing';
import { RouteName } from '@mews-commander/enums';
import { TimeFilterInterval } from '@mews-commander/data-access';
import { DateTimeService } from '@mews/framework';
import { utcToLocalizedDate } from '@mews-commander/utils';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { moduleSelector } from './module';
import { FilterDateValues, ModuleState } from './types';

export const getSidebarState = flow(moduleSelector, (moduleState: ModuleState) => moduleState.sidebar);

export const getPayoutReportUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getProp<TimeFilterInterval>()('params'),
    (resolveRoute, params) =>
        resolveRoute(RouteName.PayoutReportIndex, {
            query: params,
        })
);

const getFiltersState = createSelector(getSidebarState, (sidebar) => sidebar.filters);

export const getDefaultFilters = createSelector(getCurrentEnterpriseAppDateTimeNow, (now): TimeFilterInterval => {
    const monthAgo = DateTimeService.startOfDay(DateTimeService.addMonths(now, -1)).value;
    const endOfToday = DateTimeService.endOfDay(now).value;

    return {
        startUtc: monthAgo,
        endUtc: endOfToday,
    };
});

export const getFilters = createSelector(
    getFiltersState,
    getDefaultFilters,
    (filters, defaultFilters): TimeFilterInterval => ({
        startUtc: filters.startUtc || defaultFilters.startUtc,
        endUtc: filters.endUtc || defaultFilters.endUtc,
    })
);

export const getLocalizedFilterValues = createSelector(
    getFilters,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    ({ startUtc, endUtc }: TimeFilterInterval, timezone: string): FilterDateValues => ({
        startDate: utcToLocalizedDate(startUtc, timezone),
        endDate: utcToLocalizedDate(endUtc, timezone),
    })
);
